import React, { useCallback, useEffect, useState } from "react";
import { TbFileImport, TbFilePlus, TbHome } from "react-icons/tb";
import {
  OrderPayload,
  Workout,
} from "src/modules/fithub-shared/interfaces/workout";
import TemplatesBrowser from "src/modules/protocols/components/templates-browser";
import api, { rqueryclient } from "src/rest/api";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";

import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { SmartSensor } from "src/assets/functions/dnd";
import { useDialog } from "src/contexts/dialog-context";
import DraggableTab from "./DraggableTab";

import { AxiosResponse } from "axios";
import { ThreeDots } from "react-loader-spinner";
import { UseMutationResult } from "react-query";
import { usePlan } from "src/modules/protocols/context";
import { copy } from "./functions";
import "./styles.css";

interface TabsProps {
  handleCreate: () => void;
  create: boolean;
  reload: () => void;
  workouts: Workout[] | undefined;
  selected: string | undefined;
  select: (w: string | undefined) => void;
  onReorder: UseMutationResult<
    AxiosResponse<OrderPayload, any>,
    unknown,
    Workout[],
    unknown
  >;
}

const Tabs: React.FC<TabsProps> = ({
  workouts: values,
  handleCreate,
  create,
  select,
  reload,
  onReorder,
  selected,
}) => {
  const [workouts, setWorkouts] = useState(values);

  useEffect(() => {
    setWorkouts(values);
  }, [values]);

  const dialog = useDialog();
  const { plan } = usePlan();
  const [importTemplate, setImport] = useState(false);

  const handleSelect = useCallback(
    (e: Workout) => {
      unselect();
      setTimeout(() => {
        select(e.id !== selected ? e.id : undefined);
      }, 50);
    },
    [selected, select],
  );

  const unselect = useCallback(() => {
    if (selected) select(undefined);
    else if (create) handleCreate();
  }, [selected, create, select, handleCreate]);

  const handleDelete = useCallback(
    (del: Workout) => {
      dialog.openDialog(
        "Deletar Treino",
        "Gostaria de deletar este treino?",
        () => {
          api
            .post("workouts/delete", { id: del.id })
            .then(() => reload())
            .catch(() => alert("Erro: Não foi possível deletar treino!"));
        },
      );
    },
    [dialog, reload],
  );

  const importWorkout = (id: string) =>
    copy(id, plan.selected?.id).then(() => reload());

  const duplicateWorkout = (id: string) => copy(id).then(() => reload());

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!workouts) return;

      const { active, over } = event;

      // Verifica se `over` é válido e se o `id` mudou
      if (!over || active.id === over.id) return;

      setWorkouts((prevWorkouts) => {
        if (prevWorkouts) {
          const oldIndex = prevWorkouts.findIndex((it) => it.id === active.id);
          const newIndex = prevWorkouts.findIndex((it) => it.id === over.id);

          // Reordena os itens apenas se os índices forem válidos
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const reorderedWorkouts = arrayMove(
              prevWorkouts,
              oldIndex,
              newIndex,
            ).map((e, i) => {
              e.order = i;
              return e;
            });

            onReorder.mutate(reorderedWorkouts);

            return reorderedWorkouts;
          }
        }
        return prevWorkouts; // Retorna o estado anterior caso nada mude
      });
    },
    [workouts, onReorder, rqueryclient],
  );

  const sensors = useSensors(
    useSensor(SmartSensor.sensor, SmartSensor.activation),
  );

  return (
    <div className="row relative flex flex-shrink-0 overflow-x-scroll p-1 pt-0">
      <DndContext
        autoScroll={{ layoutShiftCompensation: false }}
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <SortableContext
          items={workouts ?? []}
          strategy={horizontalListSortingStrategy}
        >
          {/*HOME BUTTON*/}
          <div
            onClick={unselect}
            className={`tab tab-button h-12 w-14 ${
              !selected && !create && "selected"
            }`}
          >
            <TbHome size={22} />
          </div>
          {/*CREATE BUTTON*/}
          <div
            onClick={handleCreate}
            className={`tab tab-button h-12 w-14 ${create && "selected"}`}
          >
            <TbFilePlus size={22} />
            <p className="text-xs font-bold">CRIAR</p>
          </div>
          {/*IMPORT BUTTON*/}
          <div
            onClick={() => setImport(!importTemplate)}
            className={`tab tab-button h-12 w-20 ${importTemplate && "selected"}`}
          >
            <TbFileImport size={22} />
            <p className="text-xs font-bold">IMPORTAR</p>
          </div>

          {workouts ? (
            workouts.map((e) => (
              <DraggableTab
                key={e.id}
                content={e}
                onClick={() => handleSelect(e)}
                onDelete={handleDelete}
                selected={e.id === selected}
                onDuplicate={duplicateWorkout}
              />
            ))
          ) : (
            <div
              onClick={() => setImport(!importTemplate)}
              className={`tab tab-button h-12 w-20`}
            >
              <ThreeDots height={40} width={40} color="black" />
            </div>
          )}
        </SortableContext>
      </DndContext>
      <TemplatesBrowser
        active={importTemplate}
        type="Workout"
        headerTitle="Importar Treino Template (Cópia)"
        onSelect={(e, i) => importWorkout(i)}
        onDismis={setImport}
      />
    </div>
  );
};

export default Tabs;
